import request from 'sb-request'
import actions from 'redux/actions'
import { reducers, getState } from 'redux/core'
import { constants, getGqlQuery, array } from 'common/helpers'
import { getCookieValue, getFeatureToggle } from 'helpers'

import skincareGqlParams from './util/skincareGqlParams'


const selectItem    = reducers.view.pickPage.selectItem
const deselectItem  = reducers.view.pickPage.deselectItem
const setFetching   = reducers.view.pickPage.setFetching
const clearProducts = reducers.view.pickPage.clearProducts

/**
 * Clear selected products from store and localStorage
 */
const clearSelectedItems = () => {
  actions.misc.localStorage.removeItem(constants.localStorageNames.pickYourScentFlowItems)
  reducers.view.pickPage.clearSelectedItems()
}

/**
 * Saves selected items to localStorage
 */
const saveSelectedItemsToStorage = () => {
  const { view: { pickPage: { selectedItems } } } = getState()

  const isPremiumProductsForNotSubscribedEnabled = (
    getFeatureToggle(constants.features.premiumProductsForNotSubscribed)
    && /\.com(\/men|\/)$/.test(getCookieValue(constants.cookieNames.referrerLandingPageURL))
  )

  const cleanedSelectedItems = selectedItems.map(({ gender, ...other }) => ({ ...other }))

  if (isPremiumProductsForNotSubscribedEnabled) {
    actions.misc.localStorage.setSessionItem(constants.localStorageNames.pickYourScentFlowItems, cleanedSelectedItems)
  }
  else {
    actions.misc.localStorage.setItem(constants.localStorageNames.pickYourScentFlowItems, cleanedSelectedItems)
  }
}

/**
 * Get selected items from localStorage
 * We need to send items in register rest and event rest on Subscribe page
 *
 * @returns {Array} items
 */
const getSelectedItemsFromStorage = () => {
  moveSelectedItemsFromCookieToStorage()

  const isPremiumProductsForNotSubscribedEnabled = (
    getFeatureToggle(constants.features.premiumProductsForNotSubscribed)
    && /\.com(\/men|\/)$/.test(getCookieValue(constants.cookieNames.referrerLandingPageURL))
  )

  return isPremiumProductsForNotSubscribedEnabled
    ? actions.misc.localStorage.getSessionItem(constants.localStorageNames.pickYourScentFlowItems, [])
    : actions.misc.localStorage.getItem(constants.localStorageNames.pickYourScentFlowItems, [])
}

/**
 * Get selected items ids from localStorage
 * We need to send items in register rest and event rest on Subscribe page
 *
 * @returns {Array} items ids
 */
const getSelectedItemsIdsFromStorage = () => {
  const selectedItems = getSelectedItemsFromStorage()

  return selectedItems
    .map((item) => typeof item === 'number' ? item : item.id)
    .filter((id) => Boolean(id))
}

const moveSelectedItemsFromCookieToStorage = () => {
  const selectedItems = (getCookieValue(constants.cookieNames.postponed.pickYourScentFlowItems) || [])
    .filter((item) => {
      if (item && item.id) {
        return Boolean(item.id)
      }

      return false
    })

  if (selectedItems.length) {
    actions.misc.localStorage.setItem(constants.localStorageNames.pickYourScentFlowItems, selectedItems)
    actions.misc.cookies.removeItem(constants.cookieNames.postponed.pickYourScentFlowItems)
  }
}

const setSelectedItemsFromStorage = () => {
  const selectedItems = getSelectedItemsFromStorage()

  reducers.view.pickPage.setSelectedItems(selectedItems)
}

/**
 * Fetches "brands" by gender
 *
 * @returns {Promise} request
 */
const fetchBrandsByAlphabet = () => {
  const { models: { me: { gender } } } = getState()

  return request.post('graphql?name=perfumesByBrands', {
    body: {
      query: `{
        brandsLists: ${getGqlQuery('perfumesByBrand', { gender })} {
          brand
          logo
        }
      }`,
    },
    modifyResult: ({ brandsLists = [] }) => {
      const brandsByAlphabet = {}

      brandsLists
        .map(({ brand, logo }) => {
          const brandFirstLetter = /[A-Z]/.test(brand[0]) ? brand[0] : brand[1]

          return {
            logo,
            brand,
            brandFirstLetter,
          }
        })
        .sort((a, b) => {
          if (a.brandFirstLetter === b.brandFirstLetter) {
            return 0
          }

          return a.brandFirstLetter < b.brandFirstLetter ? -1 : 1
        })
        .forEach(({ logo, brand, brandFirstLetter }) => {

          if (brandsByAlphabet[brandFirstLetter]) {
            brandsByAlphabet[brandFirstLetter].push({ brand, logo })
          }
          else {
            brandsByAlphabet[brandFirstLetter] = [ { brand, logo } ]
          }
        })

      return brandsByAlphabet
    },
  })
    .then((brandsByAlphabet) => reducers.view.pickPage.setBrandsByAlphabet(brandsByAlphabet))
}

/**
 * Fetches products by brand filtered by gender
 *
 * @returns {Promise} request
 */
const fetchProductsByBrand = (brand) => {
  const { models: { me: { gender } } } = getState()

  const isPremiumProductsForNotSubscribedEnabled = (
    getFeatureToggle(constants.features.premiumProductsForNotSubscribed)
    && /\.com(\/men|\/)$/.test(getCookieValue(constants.cookieNames.referrerLandingPageURL))
  )

  let productsQueryParams = {
    gender,
    types: [],
    brands: [ brand ],
    groups: [ 'Perfume' ],
    productsFeed: 'ALL_PERFUMES',
    availableForQueue: true,
    hideUpchargeProducts: true,
  }

  if (isPremiumProductsForNotSubscribedEnabled) {
    productsQueryParams = {
      ...productsQueryParams,
      hideUpchargeProducts: false,
      minimumLepAvailable: 5,
    }
  }

  return request.post('graphql?name=fetchProductsByBrand', {
    body: {
      query: `{
        ${getGqlQuery('products', productsQueryParams)} {
          id
          image
          name
          category
          sex
        }
      }`,
    },
    modifyResult: ({ products }) => products.map((product) => ({ ...product, brand })),
  })
    .then((products) => reducers.view.pickPage.updateProductRecommendations({ id: brand, products }))
}

/**
 * Fetches similar products
 *
 * @returns {Promise} request
 */
const fetchSimilarProducts = (id) => {
  const { view: { pickPage: { productRecommendations } } } = getState()

  if (productRecommendations[id]) {
    return Promise.resolve()
  }

  return request.post('graphql?name=recommendationsByCategories', {
    body: {
      query: `{
        recommendationsByCategories(productId: ${id}){
          similar(limit: 10) {
            products {
              id
              image
              name
              brand
              category
              sex
            }
          }
        }
      }`,
    },
    modifyResult: ({ recommendationsByCategories }) => {
      const { similar } = recommendationsByCategories || {}

      return !similar ? [] : similar.products
    },
  })
    .then((products) => reducers.view.pickPage.updateProductRecommendations({ id, products }))
}

/**
 * Fetches products inside "Perfume" group filtered with tags gender
 *
 * @returns {Promise} request
 */
const fetchPopularItemsByTags = (popularTags) => {
  const { models: { me: { gender } } } = getState()

  const isPremiumProductsForNotSubscribedEnabled = (
    getFeatureToggle(constants.features.premiumProductsForNotSubscribed)
    && /\.com(\/men|\/)$/.test(getCookieValue(constants.cookieNames.referrerLandingPageURL))
  )

  let productsQueryParams = {
    gender,
    limit: 15,
    availableForQueue: true,
    hideUpchargeProducts: true,
  }

  if (isPremiumProductsForNotSubscribedEnabled) {
    productsQueryParams = {
      ...productsQueryParams,
      hideUpchargeProducts: false,
      minimumLepAvailable: 5,
    }
  }

  let query = ''

  popularTags.forEach((tagName) => {
    query += `
      ${tagName}: ${getGqlQuery('products', { ...productsQueryParams, tagNames: [ tagName ] })} {
        id
        image
        name
        brand
        category
      },
    `
  })

  return request.post('graphql?name=fetchPopularItemsByTags', {
    body: {
      query: `{${query}}`,
    },
    modifyResult: (productsByTag) => {
      const products = []

      Object.keys(productsByTag).forEach((tag) =>
        productsByTag[tag].forEach((product) => products.push({ ...product, tag }))
      )

      return products
    },
  })
    .then((products) => reducers.view.pickPage.setProducts(products))
}

/**
 * Fetches "bestsellers" products (limited by 72) inside "Perfume" group filtered with gender
 *
 * @param {Object} params
 * @param {'male'|'female'} params.gender
 * @param {String} params.category
 * @param {Number} params.limit
 * @param {Number} params.offset
 *
 * @returns {Promise} request
 */
const fetchPopularItemsByGender = (params) => {
  const { view: { pickPage: { isFetching } } } = getState()

  if (isFetching) {
    return
  }

  let {
    gender, category = 'popularity', groups = [ 'Perfume' ], limit = 72, offset = 0,
    productsFeed = 'PICK_ORDER_BRAND_PARTNERS',
  } = params

  if (category === 'bestsellers') {
    category = 'popularity'
  }

  reducers.view.pickPage.setFetching(true)

  const isPremiumProductsForNotSubscribedEnabled = (
    getFeatureToggle(constants.features.premiumProductsForNotSubscribed)
    && /\.com(\/men|\/)$/.test(getCookieValue(constants.cookieNames.referrerLandingPageURL))
  )

  let productsQueryParams = {
    limit,
    offset,
    gender,
    groups,
    category,
    productsFeed,
    availableForQueue: true,
    hideUpchargeProducts: true,
  }

  if (isPremiumProductsForNotSubscribedEnabled) {
    productsQueryParams = {
      ...productsQueryParams,
      hideUpchargeProducts: false,
      minimumLepAvailable: 5,
    }
  }

  request.post('graphql?name=fetchPopularItemsByGender', {
    body: {
      query: `{
        ${getGqlQuery('products', productsQueryParams)} {
          id
          image
          sex
          name
          type
          brand
          rating
          category
          upchargePrice
        }
      }`,
    },
  })
    .then(
      ({ products = [] }) => reducers.view.pickPage.updateProducts(products),
      () => {
        reducers.view.pickPage.setFetching(false)
        return Promise.reject()
      }
    )
}

const fetchPerfumesAndColognesByTags = (params) => {
  const { view: { pickPage: { isFetching } } } = getState()

  if (isFetching) {
    return
  }

  let {
    category = 'popularity',
    groups = 'Perfume',
    limit = 74,
    offset = 0,
    femaleTags = [],
    maleTags = [],
  } = params

  if (category === 'bestsellers') {
    category = 'popularity'
  }

  reducers.view.pickPage.setFetching(true)

  const isPremiumProductsForNotSubscribedEnabled = (
    getFeatureToggle(constants.features.premiumProductsForNotSubscribed)
    && /\.com(\/men|\/)$/.test(getCookieValue(constants.cookieNames.referrerLandingPageURL))
  )

  let productsQueryParams = {
    limit,
    offset,
    groups,
    category,
    productsFeed: 'PICK_ORDER_BRAND_PARTNERS',
    availableForQueue: true,
    hideUpchargeProducts: true,
  }

  if (isPremiumProductsForNotSubscribedEnabled) {
    productsQueryParams = {
      ...productsQueryParams,
      hideUpchargeProducts: false,
      minimumLepAvailable: 5,
    }
  }

  const perfumesParams = { ...productsQueryParams, gender: 'female', tagNames: femaleTags }
  const colognesParams = { ...productsQueryParams, gender: 'male', tagNames: maleTags }

  request.post('graphql?name=fetchPopularItemsByGender', {
    body: {
      query: `{
        perfumes: ${getGqlQuery('products', perfumesParams)} {
          id
          image
          sex
          name
          type
          brand
          rating
          category
          upchargePrice
        }
        colognes: ${getGqlQuery('products', colognesParams)} {
          id
          image
          sex
          name
          type
          brand
          rating
          category
          upchargePrice
        }
      }`,
    },
  })
    .then(
      ({ perfumes, colognes }) => {
        reducers.view.pickPage.setFetching(false)

        const modifiedPerfumes  = perfumes.map((perfume) => ({ ...perfume, gender: 'female' }))
        const modifiedColognes  = colognes.map((cologne) => ({ ...cologne, gender: 'male' }))
        const products          = [ ...modifiedPerfumes, ...modifiedColognes ]

        reducers.view.pickPage.setProducts(products)
      },
      () => {
        reducers.view.pickPage.setFetching(false)
        return Promise.reject()
      },
    )
}

const fetchSkincareProducts = () => {
  const isPremiumProductsForNotSubscribedEnabled = (
    getFeatureToggle(constants.features.premiumProductsForNotSubscribed)
    && /\.com(\/men|\/)$/.test(getCookieValue(constants.cookieNames.referrerLandingPageURL))
  )

  let productsQueryParams = {
    limit: 74,
    offset: 0,
    availableForQueue: true,
    hideUpchargeProducts: true,
  }

  if (isPremiumProductsForNotSubscribedEnabled) {
    productsQueryParams = {
      ...productsQueryParams,
      hideUpchargeProducts: false,
      minimumLepAvailable: 5,
    }
  }

  const itemFields = `
    id
    image
    sex
    name
    type
    brand
    rating
    category
    upchargePrice
  `

  const queries = skincareGqlParams.map(({ customName, ...gqlParams }) => `
    ${customName}: ${getGqlQuery('products', { ...productsQueryParams, ...gqlParams })} {
      ${itemFields}
    }
  `)

  const query = `{
    ${queries.join('')}
  }`

  reducers.view.pickPage.setFetching(true)

  return request.post('graphql?name=fetchSkincareProducts', {
    body: { query },
  })
    .then((data) => {
      const products = Object.keys(data)
        .reduce((result, key) => ([
          ...result,
          ...data[key].map((item) => ({
            ...item,
            tag: key.replace(/_.*/, ''), // face_SOMETHING => face
          })),
        ]), [])

      reducers.view.pickPage.setProducts(array.shuffle(products))
    })
    .finally(() => reducers.view.pickPage.setFetching(false))
}

// TODO remove popularProducts when backend will return 12 products for quizRecommendations
const fetchRecommendations = () => {
  const isPremiumProductsForNotSubscribedEnabled = (
    getFeatureToggle(constants.features.premiumProductsForNotSubscribed)
    && /\.com(\/men|\/)$/.test(getCookieValue(constants.cookieNames.referrerLandingPageURL))
  )

  let productsQueryParams = {
    limit: 6,
    groups: 'Perfume',
    category: 'popularity',
    productsFeed: 'PICK_ORDER_BRAND_PARTNERS',
    availableForQueue: true,
    hideUpchargeProducts: true,
  }

  if (isPremiumProductsForNotSubscribedEnabled) {
    productsQueryParams = {
      ...productsQueryParams,
      hideUpchargeProducts: false,
      minimumLepAvailable: 5,
    }
  }

  return request.post('graphql?name=quizRecommendations', {
    body: {
      query: `{
        ${getGqlQuery('products', productsQueryParams)} {
          id
          image
          name
          brand
          upchargePrice
        }
        quizRecommendations(input: { quizType: ACQUISITION }) {
          id
          image
          name
          brand
          upchargePrice
        }
      }`,
    },
    modifyResult: ({ products, quizRecommendations }) => {
      const quizRecommendationsIds  = quizRecommendations.map(({ id }) => id)
      const filteredProducts        = products.filter(({ id }) => !quizRecommendationsIds.includes(id))

      return quizRecommendations.concat(filteredProducts).slice(0, 12)
    },
  })
    .then((products) => {
      reducers.view.pickPage.setProducts(products)
      products.forEach((item) => selectItem(item))
      saveSelectedItemsToStorage()
      reducers.view.pickPage.setFetching(false)
    })
}

const sortSelectedItems = () => {
  const { view: { pickPage: { selectedItems } } } = getState()

  const body = {
    id: selectedItems.map(({ id }) => id),
    sort: {
      value: 'PICK_PAGE',
      isEnum: true,
    },
  }

  return request.post('graphql?name=products', {
    body: {
      query: `{
        ${getGqlQuery('products', { input: body })} {
          id
          sex
          name
          type
          image
          brand
          rating
          category
          upchargePrice
        }
      }`,
    },
  })
    .then(({ products }) => {
      reducers.view.pickPage.setSelectedItems(products)
      saveSelectedItemsToStorage()
    })
}


export default {
  selectItem,
  deselectItem,
  setFetching,
  saveSelectedItemsToStorage,
  getSelectedItemsIdsFromStorage,
  clearSelectedItems,
  clearProducts,
  sortSelectedItems,
  setSelectedItemsFromStorage,
  fetchRecommendations,
  fetchPerfumesAndColognesByTags,
  fetchProductsByBrand,
  fetchSimilarProducts,
  fetchBrandsByAlphabet,
  fetchPopularItemsByTags,
  fetchPopularItemsByGender,
  fetchSkincareProducts,
}
